/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { Modal, Text, Box, Stack, Group, Divider, Badge, Button, ActionIcon } from '@mantine/core';
import { IconPlaneDeparture, IconCalendarEvent, IconPlane, IconBuildingSkyscraper, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { openInNewTab } from 'utils/card-helpers';
import { radius } from 'theme';

const buildKayakFlightUrl = (destinationIata, departureDate, returnDate) => {
  const base = `https://KAYAK.com/in?a=kan_313962_590072&url=/flights/-${destinationIata}/${departureDate}`;
  return returnDate ? `${base}/${returnDate}?sort=price_a` : `${base}?sort=price_a`;
};

const buildKayakHotelUrl = (destinationIata, departureDate, returnDate) => {
  const checkOut = returnDate || dayjs(departureDate).add(1, 'day').format('YYYY-MM-DD');
  return `https://KAYAK.com/in?a=kan_313962_590072&url=/hotels/${destinationIata}/${departureDate}/${checkOut}`;
};

const buildGoogleFlightsUrl = (origin, destination, departureDate, returnDate) => {
  const base = `https://www.google.com/travel/flights?q=flights+from+${encodeURIComponent(origin)}+to+${encodeURIComponent(destination)}+on+${departureDate}`;
  return returnDate ? `${base}+return+${returnDate}` : base;
};

const FlightDealNotificationDialog = ({ open, onClose, metadata }) => {
  if (!metadata) return null;

  const { origin, destination, destination_iata, price, departure_date, return_date } = metadata;
  const isRoundTrip = Boolean(return_date);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      size="xs"
      radius={radius.surface}
      title={
        <Group gap="xs">
          <IconPlaneDeparture size={20} color="var(--mantine-color-coral-6)" />
          <Text fw={600} size="lg">
            Flight Deal Found
          </Text>
        </Group>
      }
      styles={{
        header: { paddingBottom: 4 },
        close: { display: 'none' }
      }}
    >
      {/* Custom close button */}
      <ActionIcon
        size="sm"
        variant="subtle"
        color="gray"
        onClick={onClose}
        aria-label="Close"
        style={{ position: 'absolute', top: 16, right: 16 }}
      >
        <IconX size={16} />
      </ActionIcon>

      {/* Route and Price */}
      <Box ta="center" py="md">
        <Group gap="xs" justify="center" mb={4}>
          <Text fw={600} size="xl">
            {origin}
          </Text>
          <IconPlaneDeparture size={20} color="var(--mantine-color-coral-6)" />
          <Text fw={600} size="xl">
            {destination}
          </Text>
        </Group>
        {destination_iata && (
          <Text size="xs" c="dimmed">
            ({destination_iata})
          </Text>
        )}
        <Box mt="md" mb={4}>
          <Badge color="coral" size="lg" variant="filled" style={{ fontWeight: 700, fontSize: '1.1rem', padding: '4px 12px' }}>
            ${price}
          </Badge>
        </Box>
        <Text size="xs" c="dimmed">
          {isRoundTrip ? 'Round Trip' : 'One Way'}
        </Text>
      </Box>

      <Divider my={4} />

      {/* Dates */}
      <Stack gap="sm" py="xs">
        <Group gap="sm">
          <IconCalendarEvent size={20} color="var(--mantine-color-coral-6)" />
          <Box>
            <Text size="sm" fw={600}>
              {dayjs(departure_date).format('ddd, MMM D, YYYY')}
            </Text>
            <Text size="xs" c="dimmed">
              Departure
            </Text>
          </Box>
        </Group>
        {return_date && (
          <Group gap="sm">
            <IconCalendarEvent size={20} color="var(--mantine-color-coral-6)" />
            <Box>
              <Text size="sm" fw={600}>
                {dayjs(return_date).format('ddd, MMM D, YYYY')}
              </Text>
              <Text size="xs" c="dimmed">
                Return
              </Text>
            </Box>
          </Group>
        )}
      </Stack>

      <Divider
        my="sm"
        label={
          <Group gap={4}>
            <IconPlane size={16} color="var(--mantine-color-coral-6)" />
            <Text size="xs" fw={600}>
              Book your flight
            </Text>
          </Group>
        }
        labelPosition="center"
      />

      <Group gap="xs" grow>
        {destination_iata && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openInNewTab(buildKayakFlightUrl(destination_iata, departure_date, return_date))}
          >
            Kayak.com
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => openInNewTab(buildGoogleFlightsUrl(origin, destination, departure_date, return_date))}
        >
          Google Flights
        </Button>
      </Group>

      {destination_iata && (
        <>
          <Divider
            my="sm"
            label={
              <Group gap={4}>
                <IconBuildingSkyscraper size={16} color="var(--mantine-color-coral-6)" />
                <Text size="xs" fw={600}>
                  Check hotels
                </Text>
              </Group>
            }
            labelPosition="center"
          />
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => openInNewTab(buildKayakHotelUrl(destination_iata, departure_date, return_date))}
          >
            Kayak Hotels
          </Button>
        </>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onClose}>
          Close
        </Button>
      </Group>
    </Modal>
  );
};

FlightDealNotificationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  metadata: PropTypes.shape({
    origin: PropTypes.string,
    destination: PropTypes.string,
    destination_iata: PropTypes.string,
    price: PropTypes.string,
    departure_date: PropTypes.string,
    return_date: PropTypes.string
  })
};

export default FlightDealNotificationDialog;
